/* eslint-disable react/no-unescaped-entities */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Coffee, MapPin, Calendar, Clock, X, Check, Loader2 } from 'lucide-react';
import axios from 'axios';
import salonPriveTheme from '../lib/salonPriveTheme';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Sabril's tour cities — must stay in sync with LiveSabrilCounter/MeetingFlow.
// start/end are ISO dates used to constrain the date picker (min/max) so users
// can only pick a day where Sabril is physically in that city.
const SABRIL_TOUR = [
  { city: 'Marrakech',    flag: '🇲🇦', start: '2026-07-01', end: '2026-07-15', window: '1 — 15 juillet 2026',  current: true  },
  { city: 'Marbella',     flag: '🇪🇸', start: '2026-07-15', end: '2026-07-22', window: '15 — 22 juillet 2026', current: false },
  { city: 'Cannes',       flag: '🇫🇷', start: '2026-07-22', end: '2026-07-30', window: '22 — 30 juillet 2026', current: false },
  { city: 'Saint-Tropez', flag: '🇫🇷', start: '2026-08-01', end: '2026-08-08', window: '1 — 8 août 2026',       current: false },
  { city: 'Mykonos',      flag: '🇬🇷', start: '2026-08-08', end: '2026-08-18', window: '8 — 18 août 2026',      current: false },
  { city: 'Ibiza',        flag: '🇪🇸', start: '2026-08-18', end: '2026-08-28', window: '18 — 28 août 2026',     current: false },
];

const TIME_SLOTS = ['10:00', '14:00', '16:00', '18:00'];

const theme = salonPriveTheme;

export default function CafeBookingCard() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    city: SABRIL_TOUR[0].city,
    date: '',
    time_slot: '',
    first_name: '',
    last_name: '',
    phone: '',
    restaurant_name: '',
    message: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [error, setError] = useState('');
  const [monthCount, setMonthCount] = useState(12);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/cafe-booking/stats`)
      .then((res) => {
        if (alive && typeof res.data?.month_count === 'number') {
          setMonthCount(res.data.month_count);
        }
      })
      .catch(() => { /* keep fallback 12 */ });
    return () => { alive = false; };
  }, []);

  const current = SABRIL_TOUR.find((c) => c.current);
  const selectedCity = SABRIL_TOUR.find((c) => c.city === form.city) || SABRIL_TOUR[0];

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const setCity = (city) => {
    setForm((s) => {
      const tour = SABRIL_TOUR.find((c) => c.city === city);
      const dateOk = s.date && tour && s.date >= tour.start && s.date <= tour.end;
      return { ...s, city, date: dateOk ? s.date : '' };
    });
  };

  const canSubmit =
    form.first_name.trim().length > 0 &&
    form.last_name.trim().length > 0 &&
    form.phone.trim().length >= 4 &&
    form.restaurant_name.trim().length > 0 &&
    form.city.trim().length > 0 &&
    form.date.trim().length > 0;

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    setError('');
    try {
      const res = await axios.post(`${API}/cafe-booking`, {
        ...form,
        source: 'prsnl-cafe-booking',
      });
      if (res.data?.ok) {
        try { navigator.vibrate?.(40); } catch (_) { /* noop */ }
        setDone(res.data.id);
      } else {
        setError('Une erreur est survenue, réessaie.');
      }
    } catch (err) {
      setError('Connexion impossible. Réessaie dans un instant.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* ───── Inline trigger card ───── */}
      <div className="px-4 mb-4" data-testid="cafe-booking-card">
        <div
          className="relative overflow-hidden"
          style={{
            background: 'linear-gradient(160deg, #FFF8F0 0%, #FFE9D6 60%, #FFD7B1 100%)',
            borderRadius: 18,
            border: '0.5px solid rgba(240,90,26,0.20)',
            boxShadow: '0 10px 28px -10px rgba(240,90,26,0.20), 0 0 0 0.5px rgba(60,60,67,0.06)',
          }}
        >
          {/* Hero proof — café IRL Marrakech */}
          <div
            className="relative w-full overflow-hidden"
            style={{ aspectRatio: '16 / 9' }}
            data-testid="cafe-booking-hero"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_827f745e-5f0c-4ca7-a380-679a565acd58/artifacts/1jrimolj_B3431376-0DB1-4596-8174-99B4D63E03D4.JPEG"
              alt="Café IRL à Marrakech avec une cliente"
              loading="lazy"
              style={{ width: '100%', height: '100%', objectFit: 'cover', objectPosition: 'center 30%', display: 'block' }}
            />
            <div
              className="absolute left-0 right-0 bottom-0"
              style={{
                background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.55) 100%)',
                padding: '24px 12px 8px',
              }}
            >
              <p style={{
                fontFamily: theme.fontMono, fontSize: 9, letterSpacing: '0.16em',
                textTransform: 'uppercase', color: 'rgba(255,255,255,0.95)', fontWeight: 700,
              }}>
                Marrakech · juillet 2026 — café au bord du bassin, sans cravate.
              </p>
            </div>
          </div>
          <div className="px-4 pt-4 pb-3">
            <div className="flex items-center gap-1.5 mb-1.5">
              <span style={{ fontSize: 14 }}>☕</span>
              <p style={{
                fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em',
                textTransform: 'uppercase', color: '#9A4115', fontWeight: 800,
              }}>
                Café IRL · réservation directe
              </p>
            </div>
            <h3 style={{
              fontFamily: theme.fontDisplay, fontSize: 20, fontWeight: 800,
              color: '#3D1F0A', letterSpacing: '-0.025em', lineHeight: 1.1, marginBottom: 6,
            }}>
              Programme ton café avec moi.
            </h3>
            <p style={{
              fontFamily: theme.fontSans, fontSize: 13, color: '#5A3520',
              lineHeight: 1.42, letterSpacing: '-0.005em', marginBottom: 12,
            }}>
              Pas besoin de passer par le chat — choisis ta ville et ta date,
              je te confirme dans la journée.
            </p>
            {current && (
              <div
                className="flex items-center gap-2 mb-3"
                style={{
                  background: 'rgba(255,255,255,0.75)',
                  border: '0.5px solid rgba(240,90,26,0.20)',
                  borderRadius: 12,
                  padding: '8px 11px',
                }}
                data-testid="cafe-booking-current-city"
              >
                <span
                  style={{
                    width: 7, height: 7, borderRadius: 999,
                    background: '#10B981', boxShadow: '0 0 0 3px rgba(16,185,129,0.20)',
                  }}
                />
                <p style={{ fontFamily: theme.fontSans, fontSize: 12, color: '#3D1F0A', lineHeight: 1.3 }}>
                  Actuellement à <strong>{current.city}</strong> {current.flag}
                  <span style={{ color: '#8A6248', marginLeft: 5 }}>· {current.window}</span>
                </p>
              </div>
            )}
            {/* Social proof — animated bookings counter */}
            <motion.div
              className="flex items-center justify-center gap-1.5 mb-2.5"
              style={{
                background: 'rgba(16,185,129,0.10)',
                border: '0.5px solid rgba(16,185,129,0.30)',
                borderRadius: 999,
                padding: '6px 11px',
              }}
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.15 }}
              data-testid="cafe-booking-social-proof"
            >
              <motion.span
                style={{
                  width: 7, height: 7, borderRadius: 999, background: '#10B981',
                  boxShadow: '0 0 0 3px rgba(16,185,129,0.25)',
                }}
                animate={{ scale: [1, 1.25, 1], opacity: [1, 0.7, 1] }}
                transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
              />
              <p style={{
                fontFamily: theme.fontSans, fontSize: 12, color: '#0F5132',
                fontWeight: 700, letterSpacing: '-0.005em',
              }}>
                <strong style={{ fontWeight: 800 }}>{monthCount} cafés</strong> programmés ce mois-ci
              </p>
            </motion.div>
            <button
              type="button"
              onClick={() => { try { navigator.vibrate?.(15); } catch (_) {/*noop*/} setOpen(true); }}
              className="w-full flex items-center justify-center gap-2 active:scale-[0.97] transition-transform"
              style={{
                background: 'linear-gradient(135deg, #F05A1A 0%, #C8421F 100%)',
                color: '#FFFFFF',
                padding: '12px 16px',
                borderRadius: 999,
                fontFamily: theme.fontSans,
                fontSize: 14,
                fontWeight: 800,
                letterSpacing: '-0.01em',
                boxShadow: '0 6px 18px -4px rgba(240,90,26,0.50), inset 0 1px 0 rgba(255,255,255,0.25)',
              }}
              data-testid="cafe-booking-open-btn"
            >
              <Coffee className="w-4 h-4" strokeWidth={2.4} />
              Choisir ma ville & ma date
            </button>
            <button
              type="button"
              onClick={() => {
                try { navigator.vibrate?.(10); } catch (_) { /* noop */ }
                try {
                  window.dispatchEvent(new CustomEvent('sabril:open', { detail: { intent: 'cafe-booking-info' } }));
                } catch (_) { /* noop */ }
              }}
              className="w-full flex items-center justify-center gap-1.5 mt-2 active:scale-[0.97] transition-transform"
              style={{
                background: 'transparent',
                color: '#9A4115',
                padding: '8px 12px',
                borderRadius: 999,
                fontFamily: theme.fontSans,
                fontSize: 12.5,
                fontWeight: 700,
                letterSpacing: '-0.005em',
                border: '0.5px solid rgba(240,90,26,0.30)',
              }}
              data-testid="cafe-booking-chat-fallback-btn"
            >
              <span style={{ fontSize: 13 }}>💬</span>
              Tu veux en savoir plus ? Passe par le chat <span aria-hidden>:)</span>
            </button>
          </div>
        </div>
      </div>

      {/* ───── Booking modal ───── */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18 }}
            className="fixed inset-0 z-[1000] flex items-end sm:items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)' }}
            data-testid="cafe-booking-modal"
            onClick={() => !submitting && !done && setOpen(false)}
          >
            <motion.div
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ type: 'spring', damping: 22, stiffness: 240 }}
              className="w-full sm:max-w-[420px] sm:rounded-3xl overflow-hidden"
              style={{
                background: '#FFFFFF',
                borderRadius: '24px 24px 0 0',
                maxHeight: '90vh',
                overflowY: 'auto',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="sticky top-0 z-10 flex items-center justify-between px-5 pt-4 pb-3" style={{ background: '#FFFFFF', borderBottom: '0.5px solid rgba(60,60,67,0.10)' }}>
                <div>
                  <p style={{ fontFamily: theme.fontMono, fontSize: 9.5, letterSpacing: '0.20em', textTransform: 'uppercase', color: '#F05A1A', fontWeight: 800 }}>
                    ☕ Programmer mon café
                  </p>
                  <h2 style={{ fontFamily: theme.fontDisplay, fontSize: 18, fontWeight: 800, color: '#1a1a1a', letterSpacing: '-0.02em', marginTop: 2 }}>
                    {done ? 'C\'est noté ✨' : 'Quelques infos rapides'}
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => !submitting && setOpen(false)}
                  aria-label="Fermer"
                  className="active:scale-90 transition-transform"
                  style={{ width: 32, height: 32, borderRadius: 999, background: 'rgba(60,60,67,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                  data-testid="cafe-booking-close"
                >
                  <X className="w-4 h-4" strokeWidth={2.5} />
                </button>
              </div>

              {/* Success state */}
              {done ? (
                <div className="px-5 pt-6 pb-8 flex flex-col items-center text-center">
                  <span
                    className="flex items-center justify-center mb-4"
                    style={{ width: 64, height: 64, borderRadius: 999, background: 'linear-gradient(135deg, #10B981 0%, #047857 100%)', boxShadow: '0 8px 18px -4px rgba(16,185,129,0.45)' }}
                  >
                    <Check className="w-7 h-7" strokeWidth={3} style={{ color: '#FFFFFF' }} />
                  </span>
                  <p style={{ fontFamily: theme.fontDisplay, fontSize: 20, fontWeight: 800, color: '#1a1a1a', marginBottom: 6, letterSpacing: '-0.02em' }}>
                    Ton café est noté.
                  </p>
                  <p style={{ fontFamily: theme.fontSans, fontSize: 13.5, color: '#5A5A5A', lineHeight: 1.45, marginBottom: 16 }}>
                    Je reviens vers toi par WhatsApp dans la journée pour confirmer le lieu et l'heure exacts à <strong>{form.city}</strong>.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setOpen(false); setTimeout(() => setDone(null), 300); }}
                    style={{ fontFamily: theme.fontSans, fontSize: 13, fontWeight: 700, color: '#F05A1A', padding: '10px 18px', borderRadius: 999, border: '0.5px solid rgba(240,90,26,0.30)' }}
                    data-testid="cafe-booking-done-close"
                  >
                    Parfait, à bientôt
                  </button>
                </div>
              ) : (
                <form className="px-5 pt-4 pb-6 flex flex-col gap-3.5" onSubmit={handleSubmit} data-testid="cafe-booking-form">
                  {/* City selector */}
                  <div>
                    <label className="flex items-center gap-1.5 mb-1.5" style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>
                      <MapPin className="w-3 h-3" strokeWidth={2.4} />
                      Ville du café
                    </label>
                    <div className="flex flex-wrap gap-1.5" data-testid="cafe-booking-cities">
                      {SABRIL_TOUR.map((c) => {
                        const sel = form.city === c.city;
                        return (
                          <button
                            type="button"
                            key={c.city}
                            onClick={() => setCity(c.city)}
                            className="active:scale-[0.96] transition-transform"
                            style={{
                              padding: '7px 11px',
                              borderRadius: 999,
                              fontFamily: theme.fontSans,
                              fontSize: 12,
                              fontWeight: 700,
                              letterSpacing: '-0.005em',
                              background: sel ? '#1a1a1a' : '#F5F3F0',
                              color: sel ? '#FFFFFF' : '#1a1a1a',
                              border: sel ? 'none' : '0.5px solid rgba(60,60,67,0.10)',
                              whiteSpace: 'nowrap',
                            }}
                            data-testid={`cafe-booking-city-${c.city.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {c.flag} {c.city}
                          </button>
                        );
                      })}
                    </div>
                    <p style={{ fontFamily: theme.fontSans, fontSize: 11, color: '#8A8A8A', marginTop: 6, lineHeight: 1.4 }}>
                      {SABRIL_TOUR.find((c) => c.city === form.city)?.window}
                    </p>
                  </div>

                  {/* Date + time row */}
                  <div className="flex gap-2.5">
                    <div className="flex-1">
                      <label className="flex items-center gap-1.5 mb-1.5" style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>
                        <Calendar className="w-3 h-3" strokeWidth={2.4} />
                        Date
                      </label>
                      <input
                        type="date"
                        value={form.date}
                        onChange={(e) => set('date', e.target.value)}
                        min={selectedCity.start}
                        max={selectedCity.end}
                        required
                        style={{
                          width: '100%', padding: '10px 12px', borderRadius: 10,
                          border: '0.5px solid rgba(60,60,67,0.20)', background: '#F5F3F0',
                          fontFamily: theme.fontSans, fontSize: 13, color: '#1a1a1a',
                        }}
                        data-testid="cafe-booking-date-input"
                      />
                      <p style={{ fontFamily: theme.fontSans, fontSize: 10, color: '#8A8A8A', marginTop: 4, lineHeight: 1.35 }}>
                        Entre {selectedCity.window.replace(/ 20\d{2}/, '')} ·
                        <span style={{ marginLeft: 3 }}>uniquement quand je suis à {selectedCity.city}</span>
                      </p>
                    </div>
                    <div className="flex-1">
                      <label className="flex items-center gap-1.5 mb-1.5" style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>
                        <Clock className="w-3 h-3" strokeWidth={2.4} />
                        Heure
                      </label>
                      <div className="flex flex-wrap gap-1">
                        {TIME_SLOTS.map((t) => {
                          const sel = form.time_slot === t;
                          return (
                            <button
                              type="button"
                              key={t}
                              onClick={() => set('time_slot', sel ? '' : t)}
                              style={{
                                padding: '7px 9px',
                                borderRadius: 8,
                                fontFamily: theme.fontMono,
                                fontSize: 11.5,
                                fontWeight: 700,
                                background: sel ? '#F05A1A' : '#F5F3F0',
                                color: sel ? '#FFFFFF' : '#1a1a1a',
                                border: sel ? 'none' : '0.5px solid rgba(60,60,67,0.10)',
                              }}
                              data-testid={`cafe-booking-time-${t.replace(':', '')}`}
                            >
                              {t}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  </div>

                  {/* Identity */}
                  <div className="grid grid-cols-2 gap-2.5">
                    <div>
                      <label style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>Prénom</label>
                      <input
                        type="text"
                        value={form.first_name}
                        onChange={(e) => set('first_name', e.target.value)}
                        placeholder="Sébastien"
                        required
                        style={{ width: '100%', marginTop: 4, padding: '10px 12px', borderRadius: 10, border: '0.5px solid rgba(60,60,67,0.20)', background: '#F5F3F0', fontFamily: theme.fontSans, fontSize: 13, color: '#1a1a1a' }}
                        data-testid="cafe-booking-first-name"
                      />
                    </div>
                    <div>
                      <label style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>Nom</label>
                      <input
                        type="text"
                        value={form.last_name}
                        onChange={(e) => set('last_name', e.target.value)}
                        placeholder="Dupont"
                        required
                        style={{ width: '100%', marginTop: 4, padding: '10px 12px', borderRadius: 10, border: '0.5px solid rgba(60,60,67,0.20)', background: '#F5F3F0', fontFamily: theme.fontSans, fontSize: 13, color: '#1a1a1a' }}
                        data-testid="cafe-booking-last-name"
                      />
                    </div>
                  </div>

                  {/* Phone + Restaurant */}
                  <div>
                    <label style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>Téléphone (WhatsApp)</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set('phone', e.target.value)}
                      placeholder="+33 6 …"
                      required
                      style={{ width: '100%', marginTop: 4, padding: '10px 12px', borderRadius: 10, border: '0.5px solid rgba(60,60,67,0.20)', background: '#F5F3F0', fontFamily: theme.fontSans, fontSize: 13, color: '#1a1a1a' }}
                      data-testid="cafe-booking-phone"
                    />
                  </div>
                  <div>
                    <label style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>Nom du restaurant</label>
                    <input
                      type="text"
                      value={form.restaurant_name}
                      onChange={(e) => set('restaurant_name', e.target.value)}
                      placeholder="Maison Avocado"
                      required
                      style={{ width: '100%', marginTop: 4, padding: '10px 12px', borderRadius: 10, border: '0.5px solid rgba(60,60,67,0.20)', background: '#F5F3F0', fontFamily: theme.fontSans, fontSize: 13, color: '#1a1a1a' }}
                      data-testid="cafe-booking-restaurant"
                    />
                  </div>
                  {/* Optional message */}
                  <div>
                    <label style={{ fontFamily: theme.fontMono, fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#6B6B6B', fontWeight: 700 }}>
                      Sujet du café <span style={{ textTransform: 'none', letterSpacing: 0, color: '#A8A8A8', fontWeight: 500 }}>(optionnel)</span>
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => set('message', e.target.value)}
                      placeholder="Ex: je veux discuter de mon ouverture beach club…"
                      rows={3}
                      style={{ width: '100%', marginTop: 4, padding: '10px 12px', borderRadius: 10, border: '0.5px solid rgba(60,60,67,0.20)', background: '#F5F3F0', fontFamily: theme.fontSans, fontSize: 13, color: '#1a1a1a', resize: 'none' }}
                      data-testid="cafe-booking-message"
                    />
                  </div>

                  {error && (
                    <p style={{ color: '#C8421F', fontFamily: theme.fontSans, fontSize: 12.5, padding: '8px 10px', background: 'rgba(200,66,31,0.06)', borderRadius: 8, lineHeight: 1.4 }}>
                      {error}
                    </p>
                  )}

                  <button
                    type="submit"
                    disabled={!canSubmit || submitting}
                    className="active:scale-[0.97] transition-transform"
                    style={{
                      marginTop: 2,
                      background: canSubmit && !submitting
                        ? 'linear-gradient(135deg, #F05A1A 0%, #C8421F 100%)'
                        : 'rgba(60,60,67,0.20)',
                      color: '#FFFFFF',
                      padding: '13px 18px',
                      borderRadius: 999,
                      fontFamily: theme.fontSans,
                      fontSize: 14,
                      fontWeight: 800,
                      letterSpacing: '-0.005em',
                      boxShadow: canSubmit && !submitting ? '0 8px 18px -4px rgba(240,90,26,0.45)' : 'none',
                      cursor: !canSubmit || submitting ? 'not-allowed' : 'pointer',
                      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                    }}
                    data-testid="cafe-booking-submit"
                  >
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" /> Envoi…
                      </>
                    ) : (
                      <>
                        ☕ Confirmer mon café
                      </>
                    )}
                  </button>
                  <p style={{ fontFamily: theme.fontSans, fontSize: 10.5, color: '#8A8A8A', lineHeight: 1.45, marginTop: 4, textAlign: 'center' }}>
                    Café offert par Sabril. Aucun appel téléphonique — confirmation par WhatsApp.
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
